import AdvancedSearch from '@app/components/AdvancedSearch'
import BidirectionalSort from '@app/components/BidirectionalSort'
import Image from '@app/components/Image'
import InfiniteScrollTable from '@app/components/InfiniteScrollTable'
import { DevelopmentClient } from '@app/models/DevelopmentClient'
import { IDevelopmentSite } from '@app/models/DevelopmentSite'
import { ImageSize } from '@app/models/enums'
import {
  IGlobalCompanyContactsState,
  IGlobalConfigurationState,
  IGlobalDevelopmentProjectClients,
} from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { humanize } from '@app/utils/formatting'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { RouteComponentProps } from 'react-router-dom'
import { Column } from 'react-virtualized'
import { Col, FormGroup, Input, Label } from 'reactstrap'
import {
  actionCreators,
  IDevelopmentProjectsListState,
} from '../modules/developmentProjectsList'
import './DevelopmentProjectsPage.scss'

type ComponentProps = IDevelopmentProjectsListState &
  IGlobalCompanyContactsState &
  IGlobalConfigurationState &
  IGlobalDevelopmentProjectClients &
  typeof actionCreators &
  RouteComponentProps<{}>

export default class AssignmentsListPage extends React.PureComponent<ComponentProps> {
  private tableRef: React.RefObject<InfiniteScrollTable>

  constructor(props: ComponentProps) {
    super(props)

    bindFunctions(this)

    this.tableRef = React.createRef<InfiniteScrollTable>()
  }

  public render() {
    return (
      <DocumentTitle title='My Development Projects'>
        <div className='container d-flex flex-column h-100 pt-3'>
          <div>
            <h3>My Development Projects</h3>
            <AdvancedSearch className='mt-1' expand='md'>
              <Col md={4} xl={4}>
                <FormGroup>
                  <Label>Client</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.client}
                    onChange={this._onChangeClient}
                  >
                    <option key='client1' value=''>
                      All
                    </option>
                    {this.props.developmentSiteMetadata.clients
                      .filter((x) =>
                        x && x.logo && x.logo.id && x.name ? true : false,
                      )
                      .map((c: any, i) => (
                        <option key={`client-${i}`} value={c.id}>
                          {c.name}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4} xl={4}>
                <FormGroup>
                  <Label>City</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.city}
                    onChange={this._onChangeCity}
                  >
                    <option key='client1' value=''>
                      All
                    </option>
                    {this.props.developmentSiteMetadata.cities
                      .map((c: any, i) => (
                        <option key={`client-${i}`} value={c}>
                          {c}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4} xl={4}>
                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.status}
                    onChange={this._onChangeStatus}
                  >
                    <option key='client1' value=''>
                      All
                    </option>
                    {this.props.configuration.developmentSiteStatuses
                      .map((c: any, i) => (
                        <option key={`client-${i}`} value={c}>
                          {c}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4} xl={4}>
                <FormGroup>
                  <Label>Sort By</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.sortOrder}
                    onChange={this._onChangeSortOrder}
                  >
                    {this.props.configuration.developmentSiteSortOptions
                      .map((c: any, i) => (
                        <option key={`sort-${i}`} value={c}>
                          {humanize(c)}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <BidirectionalSort
                md={4}
                xl={4}
                orderBy={this.props.orderBy}
                changeOrdering={this._onChangeOrderBy}
              />
            </AdvancedSearch>
          </div>
          <div>
            <InfiniteScrollTable
              isLoading={this.props.isLoading}
              items={this.props.items}
              rowHeight={112}
              loadItems={this.props.loadDevelopmentProjects}
              noRowsRenderer={this._noRowsRenderer}
              onRowClicked={this._onRowClicked}
              totalItems={this.props.totalItems}
              ref={this.tableRef}
            >
              <Column
                cellRenderer={({ rowData }) => {
                  const site = rowData as IDevelopmentSite

                  return (
                    <div>
                      <div style={{ width: '200px' }}>
                        <Image
                          defaultImageUrl='/images/default-property.png'
                          image={site.coverPhoto}
                          size={ImageSize.h200w250}
                          width={200}
                        />
                      </div>
                    </div>
                  )
                }}
                className='d-none d-md-block'
                dataKey='name'
                style={{ marginLeft: 0 }}
                width={200}
              />
              <Column
                cellRenderer={({ rowData }) => {
                  const site = rowData as IDevelopmentSite

                  return (
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{site.name}</div>
                      <div>{site.address.line1}</div>
                      <div>
                        {`${site.address.city}, ${site.address.state} ${site.address.zip}`}
                      </div>
                    </div>
                  )
                }}
                dataKey='name'
                flexGrow={1}
                width={700}
              />
              <Column
                cellRenderer={({ rowData }) => {
                  const site = rowData as IDevelopmentSite

                  const client =
                    this.props.developmentSiteMetadata.clients.find(
                      (item: any) => {
                        return item.id === site.client.clientId
                      },
                    ) as DevelopmentClient

                  return (
                    <div>
                      {client && client.logo && (
                        <img
                          src={`${client.logo.getImageUrl(ImageSize.h100w100)}`}
                        />
                      )}
                    </div>
                  )
                }}
                dataKey='name'
                flexGrow={1}
                className='d-none d-md-block'
                width={200}
              />
              <Column
                cellRenderer={({ rowData }) => {
                  const site = rowData as IDevelopmentSite

                  return (
                    <div
                      className={`development-site-status-container ${site.status
                        .toString()
                        .toLowerCase()}`}
                    >
                      <div className='status-label'>{site.status}</div>
                    </div>
                  )
                }}
                dataKey='name'
                style={{ marginRight: 0 }}
                flexGrow={1}
                width={100}
              />
            </InfiniteScrollTable>
          </div>
        </div>
      </DocumentTitle>
    )
  }

  private _noRowsRenderer() {
    return <div>No development projects</div>
  }

  private _onRowClicked(item: IDevelopmentSite, index: number) {
    const client = this.props.developmentSiteMetadata.clients.find((c: any) => {
      return c.id === item.client.clientId
    })
    if (client) {
      this.props.history.push(
        `/development/client/${client.slug}/site/${item.slug}`,
      )
    }
  }

  private _onChangeCity(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setCity(event.target.value)
    this._reloadData()
  }

  private _onChangeClient(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setClient(event.target.value)
    this._reloadData()
  }

  private _onChangeFilter(newFilter: string) {
    this.props.setFilterText(newFilter)
    this._reloadData()
  }

  private _onChangeSortOrder(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setSortOrder(event.target.value)
    this._reloadData()
  }

  private _onChangeOrderBy(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setOrderBy(event.target.value)
    this._reloadData()
  }

  private _onChangeStatus(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setStatus(event.target.value)
    this._reloadData()
  }

  private _reloadData() {
    if (this.tableRef.current) {
      this.tableRef.current.forceReloadData()
    }
  }
}
